import type {
    ResourceAccommodation,
    ResourceAccommodationFactoryParams} from "@@/shared/acquire/resources/resource-accommodation/resource-accommodation";
import {
    resourceAccommodationFactory,
    resourceAccommodationZodObject,
} from "@@/shared/acquire/resources/resource-accommodation/resource-accommodation";
import type {
    ResourceGlobal,
    resourceGlobalTypes,
} from "@@/shared/acquire/resources/resource-global";
import type { ResourceGroup } from "@@/shared/acquire/resources/resource-group";
import type { MatchingResourceParts } from "@@/shared/acquire/resources/resource-parts";
import type { ResourceTableGroup } from "@@/shared/acquire/resources/resource-table-group";
import { toObjectMap } from "@@/utilities/dictionary";
import type { ExcludeFromTuple} from "@@/utilities/type-helpers";
import { assertTypeMatch } from "@@/utilities/type-helpers";
import { type IsEqual } from "type-fest";
import { z } from "zod";
import type {
    ResourceGeneric,
    ResourceGenericFactoryParams} from "./resource-generic";
import {
    resourceGenericFactory,
    resourceGenericZodObject,
} from "./resource-generic";
import type {
    ResourceReserved,
    ResourceReservedFactoryParams} from "./resource-reserved";
import {
    resourceReservedFactory,
    resourceReservedZodObject,
} from "./resource-reserved";
import type {
    ResourceTable,
    ResourceTableFactoryParams} from "./resource-table";
import {
    resourceTableFactory,
    resourceTableZodObject,
} from "./resource-table";

type InventoryResource = Exclude<
    ResourceGlobal,
    ResourceGroup | ResourceTableGroup
>;
const inventoryResourceZodUnion = z.union([
    resourceGenericZodObject,
    resourceTableZodObject,
    resourceAccommodationZodObject,
    resourceReservedZodObject,
]);

const inventoryResourceTypes = [
    "RESOURCE_GENERIC",
    "RESOURCE_TABLE",
    "RESOURCE_ACCOMMODATION",
    "RESOURCE_RESERVED",
] as const;
const inventoryResourceType = toObjectMap<InventoryResourceType>([
    ...inventoryResourceTypes,
]);

const inventoryResourceTypeZodEnum = z.enum(inventoryResourceTypes);
type InventoryResourceType = z.infer<typeof inventoryResourceTypeZodEnum>;

// make sure we've got all type types
// covered in the inventoryResourceTypes array
// it should contain all the types possible from
// InventoryResource._type
assertTypeMatch<
    IsEqual<InventoryResourceType, (typeof inventoryResourceTypes)[number]>
>();

// also make sure we've got all type types
// covered in the inventoryResourceTypes array by making sure
// that it contains all the types possible from
// resourceGroupTypes except "RESOURCE_GROUP"
assertTypeMatch<
    IsEqual<
        InventoryResourceType,
        ExcludeFromTuple<
            typeof resourceGlobalTypes,
            "RESOURCE_GROUP" | "RESOURCE_TABLE_GROUP"
        >[number]
    >
>();

const isInventoryResource = (item: unknown): item is InventoryResource => {
    if (!item) return false;
    if (!(item as InventoryResource)?._type) return false;
    if (typeof (item as InventoryResource)._type !== "string") return false;
    if (inventoryResourceType[(item as InventoryResource)._type]) return true;
    return false;
};

const inventoryResourceFactory = {
    resourceGeneric: resourceGenericFactory,
    resourceTable: resourceTableFactory,
    resourceAccommodation: resourceAccommodationFactory,
    resourceReserved: resourceReservedFactory,
};

type InventoryResourceFactoryParams = {
    RESOURCE_GENERIC:
        | ResourceGenericFactoryParams
        | MatchingResourceParts<ResourceGeneric>;
    RESOURCE_TABLE:
        | ResourceTableFactoryParams
        | MatchingResourceParts<ResourceTable>;
    RESOURCE_ACCOMMODATION:
        | ResourceAccommodationFactoryParams
        | MatchingResourceParts<ResourceAccommodation>;
    RESOURCE_RESERVED:
        | ResourceReservedFactoryParams
        | MatchingResourceParts<ResourceReserved>;
};

// Make sure InventoryResourceFactoryParams got all
// InventoryResourceType types covered
assertTypeMatch<
    IsEqual<keyof InventoryResourceFactoryParams, InventoryResourceType>
>();

export {
    inventoryResourceFactory,
    inventoryResourceType,
    inventoryResourceTypeZodEnum,
    inventoryResourceTypes,
    inventoryResourceZodUnion,
    isInventoryResource,
};
export type {
    InventoryResource,
    InventoryResourceFactoryParams,
    InventoryResourceType,
};
